import { useState } from 'react';
import { Modal } from './Modal';
import { DocumentCard, Document } from './DocumentCard';
import { PDFPreviewModal } from './PDFPreviewModal';

interface DocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  productId: string;
  documents: Document[];
}

/**
 * DocumentModal component - Main modal displaying document list
 * 
 * Features:
 * - Document list with all 5 document types
 * - Status badges for each document
 * - View button opens PDF preview modal (nested modal)
 * - Responsive layout
 * - Accessibility features (ARIA labels, focus management)
 * 
 * Document Types:
 * - Allocation declaration
 * - Inspection certificate
 * - Warehouse receipt(s)
 * - Certificate of origin
 * - Other documents
 */
export const DocumentModal = ({ isOpen, onClose, documents }: DocumentModalProps) => {
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [isPDFPreviewOpen, setIsPDFPreviewOpen] = useState(false);

  // Handle View button click - open PDF preview modal
  const handleViewDocument = (document: Document) => {
    setSelectedDocument(document);
    setIsPDFPreviewOpen(true);
  };

  // Handle PDF preview modal close - return to document list
  const handlePDFPreviewClose = () => {
    setIsPDFPreviewOpen(false);
    setSelectedDocument(null);
  };

  return (
    <>
      {/* Document List Modal */}
      <Modal
        isOpen={isOpen && !isPDFPreviewOpen}
        onClose={onClose}
        title="Product Documents"
        size="lg"
      >
        <div className="flex flex-col gap-4">
          {documents.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-base text-text-secondary">No documents available</p>
            </div>
          ) : (
            documents.map((document) => (
              <DocumentCard
                key={document.id}
                document={document}
                onView={() => handleViewDocument(document)}
              />
            ))
          )}
        </div>
      </Modal>

      {/* PDF Preview Modal (nested) */}
      <PDFPreviewModal
        isOpen={isPDFPreviewOpen}
        onClose={handlePDFPreviewClose}
        document={selectedDocument}
      />
    </>
  );
};

